import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { FileText, Download, AlertCircle, CheckCircle } from 'lucide-react';

interface ConvertResponse {
  success: boolean;
  message: string;
  file_id?: string;
  filename: string;
  file_size?: number;
  download_url?: string;
  processing_time: number;
}

const MarkdownConverterTool: React.FC = () => {
  const [markdownContent, setMarkdownContent] = useState('# 示例文档\n\n这是一个Markdown转Word工具的示例。\n\n## 数学公式支持\n\n行内公式：$E=mc^2$\n\n块级公式：\n$$\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}$$\n\n## 其他功能\n\n- **粗体文本**\n- *斜体文本*\n- `代码片段`\n\n### 代码块\n\n```python\ndef fibonacci(n):\n    if n <= 1:\n        return n\n    return fibonacci(n-1) + fibonacci(n-2)\n```\n\n这个工具支持完整的Markdown语法和LaTeX数学公式转换。');
  const [outputFilename, setOutputFilename] = useState('document.docx');
  const [useChineseTemplate, setUseChineseTemplate] = useState(true);
  const [isConverting, setIsConverting] = useState(false);
  const [convertResult, setConvertResult] = useState<ConvertResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConvert = async () => {
    if (!markdownContent.trim()) {
      setError('请输入Markdown内容');
      return;
    }

    setIsConverting(true);
    setError(null);
    setConvertResult(null);

    try {
      const formData = new FormData();
      formData.append('content', markdownContent);
      formData.append('output_filename', outputFilename);
      formData.append('use_chinese_template', useChineseTemplate.toString());

      const response = await fetch('/api/tools/markdown-converter/convert', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result: ConvertResponse = await response.json();
        setConvertResult(result);
      } else {
        const errorData = await response.json();
        setError(errorData.message || `转换失败: HTTP ${response.status}`);
      }
    } catch (err) {
      setError(`转换过程中发生错误: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownload = async () => {
    if (!convertResult?.download_url) return;

    try {
      const response = await fetch(`/api/tools/markdown-converter${convertResult.download_url}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = convertResult.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('下载失败，请重试');
      }
    } catch (err) {
      setError(`下载过程中发生错误: ${err instanceof Error ? err.message : '未知错误'}`);
    }
  };

  const loadExample = (example: string) => {
    const examples = {
      basic: '# 基础文档示例\n\n## 介绍\n\n这是一个基础的Markdown文档示例。\n\n### 文本格式\n\n- **粗体文本**\n- *斜体文本*\n- `行内代码`\n\n### 列表\n\n1. 第一项\n2. 第二项\n3. 第三项\n\n### 代码块\n\n```javascript\nfunction hello() {\n    console.log("Hello, World!");\n}\n```',
      math: '# 数学公式文档\n\n## 基础数学公式\n\n### 行内公式\n\n质能方程：$E=mc^2$\n\n勾股定理：$a^2 + b^2 = c^2$\n\n### 块级公式\n\n高斯积分：\n$$\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}$$\n\n矩阵乘法：\n$$\\begin{bmatrix}\na & b \\\\\nc & d\n\\end{bmatrix}\n\\begin{bmatrix}\nx \\\\\ny\n\\end{bmatrix}\n=\n\\begin{bmatrix}\nax + by \\\\\ncx + dy\n\\end{bmatrix}$$\n\n### 复杂公式\n\n欧拉公式：$e^{i\\pi} + 1 = 0$\n\n无穷级数：\n$$\\sum_{n=1}^{\\infty} \\frac{1}{n^2} = \\frac{\\pi^2}{6}$$',
      report: '# 技术报告模板\n\n**日期**: 2024年1月\n**版本**: v1.0\n**作者**: 工具门户团队\n\n## 执行摘要\n\n本报告展示了Markdown转Word工具的核心功能和技术实现。\n\n## 技术背景\n\n### 核心技术\n\n- **转换引擎**: Pandoc\n- **数学公式**: LaTeX → Word原生公式\n- **支持格式**: Markdown → DOCX\n\n### 性能指标\n\n| 指标 | 数值 |\n|------|------|\n| 转换速度 | <3秒/MB |\n| 支持文件大小 | 最大10MB |\n| 公式准确率 | >99% |\n\n## 技术实现\n\n转换流程如下：\n\n```mermaid\ngraph LR\n    A[Markdown输入] --> B[Pandoc处理]\n    B --> C[LaTeX公式转换]\n    C --> D[Word文档生成]\n```\n\n## 结论\n\n该工具成功实现了高质量的文档转换功能。'
    };
    
    setMarkdownContent(examples[example as keyof typeof examples] || examples.basic);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Markdown转Word工具
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            将Markdown文档转换为Word格式，支持LaTeX数学公式、代码高亮、表格等完整语法。
          </p>
          
          {/* 示例按钮 */}
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => loadExample('basic')}
            >
              基础示例
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => loadExample('math')}
            >
              数学公式示例
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => loadExample('report')}
            >
              技术报告示例
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 输入区域 */}
        <Card>
          <CardHeader>
            <CardTitle>Markdown输入</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="markdown-content" className="text-sm font-medium">
                Markdown内容
              </label>
              <textarea
                id="markdown-content"
                value={markdownContent}
                onChange={(e) => setMarkdownContent(e.target.value)}
                placeholder="请输入Markdown内容..."
                className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                style={{ resize: 'vertical' }}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="output-filename" className="text-sm font-medium">
                输出文件名
              </label>
              <input
                id="output-filename"
                type="text"
                value={outputFilename}
                onChange={(e) => setOutputFilename(e.target.value)}
                placeholder="document.docx"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">格式选项</label>
              <div className="flex items-center space-x-2">
                <input
                  id="chinese-template"
                  type="checkbox"
                  checked={useChineseTemplate}
                  onChange={(e) => setUseChineseTemplate(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="chinese-template" className="text-sm text-gray-700">
                  使用中文格式模板 (宋体5号，单倍行距)
                </label>
              </div>
              <p className="text-xs text-gray-500">
                启用后将自动应用中文排版规范：宋体字体、5号字体、单倍行距、首行缩进
              </p>
            </div>

            <Button 
              onClick={handleConvert}
              disabled={isConverting || !markdownContent.trim()}
              className="w-full"
            >
              {isConverting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  转换中...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  转换为Word
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* 输出区域 */}
        <Card>
          <CardHeader>
            <CardTitle>转换结果</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">转换失败</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {convertResult && convertResult.success && (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">转换成功</p>
                    <p className="text-sm text-green-600">{convertResult.message}</p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">文件名:</span>
                    <span className="font-mono">{convertResult.filename}</span>
                  </div>
                  {convertResult.file_size && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">文件大小:</span>
                      <span className="font-mono">{(convertResult.file_size / 1024).toFixed(1)} KB</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">处理时间:</span>
                    <span className="font-mono">{convertResult.processing_time.toFixed(2)}秒</span>
                  </div>
                </div>

                <Button 
                  onClick={handleDownload}
                  className="w-full"
                  variant="default"
                >
                  <Download className="h-4 w-4 mr-2" />
                  下载Word文档
                </Button>
              </div>
            )}

            {!error && !convertResult && (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>输入Markdown内容并点击转换</p>
                <p className="text-sm mt-1">支持LaTeX数学公式和完整Markdown语法</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 功能说明 */}
      <Card>
        <CardHeader>
          <CardTitle>功能说明</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">✅ 支持的功能</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• 标准Markdown语法</li>
                <li>• LaTeX数学公式 ($...$, $$...$$)</li>
                <li>• 代码块和语法高亮</li>
                <li>• 表格、列表、链接</li>
                <li>• 文本格式(粗体、斜体等)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">📋 使用提示</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• 支持最大10MB文件</li>
                <li>• 数学公式自动转换为Word原生格式</li>
                <li>• 可自定义输出文件名</li>
                <li>• 转换后文件保留24小时</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarkdownConverterTool;